import { createStore } from 'zustand';
import { devtools } from 'zustand/middleware';

// Enums
import { dtoState } from '../../../../common/enums/dtoState';

// Helpers
import { flattenDTO } from '../common/helpers/flattenDTO';
import { findRootPredicateUuid, findPredicateById } from './helpers';

export const createCriteriaStore = (initialState = {}) => {
    return createStore()(
        devtools(
            (set) => ({
                predicates: initialState,
                lastMovedUuid: undefined,
                init: (criteria, force) => {
                    set((state) => {
                        return {
                            ...state,
                            lastMovedUuid: undefined,
                            predicates: criteria ? flattenDTO(ko.toJS(criteria), true) : {}
                        };
                    });
                },
                reset: () => {
                    set((state) => {
                        return {
                            ...state,
                            lastMovedUuid: undefined,
                            predicates: initialState
                        };
                    });
                },
                updatePredicate: (predicate) => {
                    // Check if predicateParts are hydrated or just uuids
                    if (typeof predicate?.predicateParts?.[0] === 'string') {
                        set((state) => {
                            return {
                                ...state,
                                lastMovedUuid: undefined,
                                predicates: {
                                    ...state.predicates,
                                    [predicate.uuid]: predicate
                                }
                            };
                        });
                    } else {
                        // const updatedPredicate = {
                        //     ...predicate,
                        //     predicateParts: (predicate.predicateParts ?? []).map((data) => data.uuid)
                        // };
                        // const storeUpdates = {
                        //     [predicate.uuid]: updatedPredicate,
                        //     ...(predicate.predicateParts ?? []).reduce(
                        //         (acc, curr) => ({
                        //             ...acc,
                        //             [curr.uuid]: curr
                        //         }),
                        //         {}
                        //     )
                        // };

                        set((state) => {
                            return {
                                ...state,
                                lastMovedUuid: undefined,
                                predicates: {
                                    ...state.predicates,
                                    ...flattenDTO(predicate)
                                }
                            };
                        });
                    }
                },
                deletePredicate: ({ predicateUuid, parentUuid }) => {
                    set((state) => {
                        if (state.predicates[predicateUuid].dtoState === dtoState.New) {
                            const updatedState = {
                                ...state,
                                lastMovedUuid: undefined,
                                predicates: {
                                    ...state.predicates,
                                    [parentUuid]: {
                                        ...state.predicates[parentUuid],
                                        predicates: (state.predicates[parentUuid]?.predicates ?? []).filter(
                                            (uuid) => uuid !== predicateUuid
                                        )
                                    }
                                }
                            };
                            delete updatedState.predicates[predicateUuid];

                            return updatedState;
                        } else {
                            return {
                                ...state,
                                lastMovedUuid: undefined,
                                predicates: {
                                    ...state.predicates,
                                    [predicateUuid]: {
                                        ...state.predicates[predicateUuid],
                                        dtoState: dtoState.Deleted
                                    },
                                    [parentUuid]: {
                                        ...state.predicates[parentUuid]
                                    }
                                }
                            };
                        }
                    });
                },
                addPredicate: (predicate, parentPredicateUuid) => {
                    set((state) => {
                        const parentUuid = parentPredicateUuid ?? findRootPredicateUuid(state.predicates);

                        return {
                            ...state,
                            lastMovedUuid: undefined,
                            predicates: {
                                ...state.predicates,
                                [parentUuid]: {
                                    ...state.predicates[parentUuid],
                                    predicates: [...(state.predicates[parentUuid]?.predicates ?? []), predicate.uuid]
                                },
                                ...flattenDTO({ ...predicate })
                            }
                        };
                    });
                },
                addPredicatePart: (predicatePart, parentPredicateUuid) => {
                    set((state) => {
                        const parentUuid = parentPredicateUuid ?? findRootPredicateUuid(state.predicates);

                        return {
                            ...state,
                            lastMovedUuid: undefined,
                            predicates: {
                                ...state.predicates,
                                [predicatePart.uuid]: predicatePart,
                                [parentUuid]: {
                                    ...state.predicates[parentUuid],
                                    predicatePart: [
                                        ...(state.predicates[parentUuid]?.predicates ?? []),
                                        predicatePart.uuid
                                    ]
                                }
                            }
                        };
                    });
                },
                updatePredicateOrder: (parentUuid, newOrderedPredicateUuids = []) => {
                    set((state) => {
                        let orderCount = 0;
                        const updatedPredicates = newOrderedPredicateUuids.reduce((acc, curr, index) => {
                            if (state.predicates[curr].dtoState === dtoState.Deleted) return acc;
                            orderCount++;
                            return {
                                ...acc,
                                [curr]: {
                                    ...state.predicates[curr],
                                    dtoState: [dtoState.Deleted, dtoState.New].includes(state.predicates[curr].dtoState)
                                        ? state.predicates[curr].dtoState
                                        : dtoState.Updated,
                                    predicateOrder: orderCount
                                }
                            };
                        }, {});

                        return {
                            ...state,
                            lastMovedUuid: undefined,
                            predicates: {
                                ...state.predicates,
                                [parentUuid]: {
                                    ...state.predicates[parentUuid],
                                    predicates: [...newOrderedPredicateUuids]
                                },
                                ...updatedPredicates
                            }
                        };
                    });
                },
                movePredicateToGroup: ({ predicateUuid, predicateGroupUuid }) => {
                    set((state) => {
                        const criteriaPredicateUuid = findRootPredicateUuid(state.predicates);
                        const predicateBeingMoved = state.predicates[predicateUuid];
                        const targetPredicateGroup = state.predicates[predicateGroupUuid];
                        const updatedTargetPredicateGroup = {
                            ...targetPredicateGroup,
                            dtoState: dtoState.Updated,
                            predicates: [...(targetPredicateGroup.predicates ?? []), predicateUuid]
                        };
                        const updatedPredicateBeingMoved = (() => {
                            const { parentPredicateId, ...predicateProps } = { ...predicateBeingMoved };
                            const newPredicateOrder =
                                (targetPredicateGroup?.predicates ?? []).reduce((acc, curr) => {
                                    return Math.max(...[state.predicates[curr]?.predicateOrder, acc]);
                                }, 0) + 1;

                            return criteriaPredicateUuid === predicateGroupUuid
                                ? {
                                      ...predicateProps,
                                      dtoState: dtoState.Updated,
                                      predicateOrder: newPredicateOrder
                                  }
                                : {
                                      ...predicateProps,
                                      parentPredicateId: targetPredicateGroup.predicateId,
                                      dtoState: dtoState.Updated,
                                      predicateOrder: newPredicateOrder
                                  };
                        })();
                        const updatedState = {
                            ...state,
                            lastMovedUuid: predicateUuid,
                            predicates: {
                                ...state.predicates,
                                [predicateUuid]: updatedPredicateBeingMoved,
                                [predicateGroupUuid]: updatedTargetPredicateGroup
                            }
                        };

                        if (predicateBeingMoved?.parentPredicateId) {
                            const parentPredicate = Object.values(state.predicates).find((predicate) => {
                                return (
                                    predicate.predicateId === predicateBeingMoved?.parentPredicateId &&
                                    predicate.predicateType === 'PredicateGroup'
                                );
                            });

                            return {
                                ...updatedState,
                                predicates: {
                                    ...updatedState.predicates,
                                    [parentPredicate?.uuid]: {
                                        ...parentPredicate,
                                        dtoState: dtoState.Updated,
                                        predicates: (parentPredicate?.predicates ?? []).filter(
                                            (uuid) => uuid !== predicateUuid
                                        )
                                    }
                                }
                            };
                        } else {
                            const criteriaPredicate = state.predicates[criteriaPredicateUuid];

                            return {
                                ...updatedState,
                                predicates: {
                                    ...updatedState.predicates,
                                    [criteriaPredicate?.uuid]: {
                                        ...criteriaPredicate,
                                        dtoState: dtoState.Updated,
                                        predicates: (criteriaPredicate?.predicates ?? []).filter(
                                            (uuid) => uuid !== predicateUuid
                                        )
                                    }
                                }
                            };
                        }
                    });
                },
                movePredicateToRoot: ({ predicateUuid }) => {
                    set((state) => {
                        const criteriaPredicateUuid = findRootPredicateUuid(state.predicates);
                        const criteriaPredicate = state.predicates[criteriaPredicateUuid];
                        const sourceGroup = findPredicateById({
                            predicates: state.predicates,
                            predicateId: state.predicates[predicateUuid]?.parentPredicateId
                        });
                        const predicateBeingMoved = state.predicates[predicateUuid];
                        const newPredicateOrder =
                            (criteriaPredicate?.predicates ?? []).reduce((acc, curr) => {
                                return Math.max(...[state.predicates[curr]?.predicateOrder, acc]);
                            }, 0) + 1;

                        delete predicateBeingMoved.parentPredicateId;
                        return {
                            ...state,
                            lastMovedUuid: predicateUuid,
                            predicates: {
                                ...state.predicates,
                                [predicateUuid]: {
                                    ...predicateBeingMoved,
                                    dtoState: dtoState.Updated,
                                    predicateOrder: newPredicateOrder
                                },
                                [sourceGroup?.uuid]: {
                                    ...sourceGroup,
                                    dtoState: dtoState.Updated,
                                    predicates: (sourceGroup.predicates ?? []).filter((uuid) => uuid !== predicateUuid)
                                },
                                [criteriaPredicateUuid]: {
                                    ...criteriaPredicate,
                                    predicates: [...(criteriaPredicate?.predicates ?? []), predicateUuid]
                                }
                            }
                        };
                    });
                }
            }),
            {
                name: 'CriteriaStore'
            }
        )
    );
};
